/**
 * Custom OLED Detail
 *
 * Listens for two events from elsewhere on the PDP:
 *   - `custom-pdp:product-change` → switches active product group, applies
 *     accent color, activates the matching product_background bg, and
 *     re-resolves the active legacy bg (when no product_background blocks).
 *   - `custom-pdp:detail-change`  → switches active detail slot 1/2/3 AND
 *     re-resolves the active legacy bg image.
 *
 * Color sync: on init, also reads --ch-accent / --ch-accent-rgb directly
 * from the hero section (if present) and applies them immediately. This
 * protects against a race where the hero finishes its async image color
 * extraction BEFORE this script attaches its event listener — in that case
 * the broadcast event would be missed and this section would stay on the
 * default green.
 *
 * Parallax is suppressed automatically when product_background blocks are in
 * use (section has `.has-product-bg`) since per-product bgs are designed
 * to stay still.
 */
(function () {
  'use strict';

  var PRODUCT_EVENT = 'custom-pdp:product-change';
  var DETAIL_EVENT = 'custom-pdp:detail-change';

  function setActiveGroup(root, handle) {
    if (!handle) return false;
    var groups = root.querySelectorAll('.custom-oled-detail__group[data-product-handle]');
    var match = null;
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].getAttribute('data-product-handle') === handle) {
        match = groups[i];
        break;
      }
    }
    if (!match) return false;
    for (var j = 0; j < groups.length; j++) {
      var g = groups[j];
      if (g === match) {
        g.classList.add('is-active');
        g.removeAttribute('aria-hidden');
      } else {
        g.classList.remove('is-active');
        g.setAttribute('aria-hidden', 'true');
      }
    }
    return true;
  }

  function applyAccent(root, accentColor, accentRgb) {
    if (accentColor) root.style.setProperty('--co-accent', accentColor);
    if (accentRgb) root.style.setProperty('--co-accent-rgb', accentRgb);
  }

  /**
   * Mirror the hero section's currently-applied accent into this section.
   * The hero sets --ch-accent / --ch-accent-rgb on its own root inside
   * dispatchProductChange(), so we read those directly.
   */
  function syncAccentFromHero(root) {
    var hero = document.querySelector('[data-custom-hero]');
    if (!hero) return;
    var heroStyle = getComputedStyle(hero);
    var color = heroStyle.getPropertyValue('--ch-accent').trim();
    var rgb = heroStyle.getPropertyValue('--ch-accent-rgb').trim();
    applyAccent(root, color || null, rgb || null);
  }

  function setActiveDetail(root, idx) {
    if (!idx) return;
    // Only swap details inside the currently-active group, so other
    // products' groups keep their default (slot 1) active and don't
    // accidentally light up multiple slots if we ever flip back to them.
    var activeGroup = root.querySelector('.custom-oled-detail__group.is-active');
    var scope = activeGroup || root;
    var details = scope.querySelectorAll('.custom-oled-detail__detail[data-detail-index]');
    for (var i = 0; i < details.length; i++) {
      var d = details[i];
      var thisIdx = parseInt(d.getAttribute('data-detail-index'), 10);
      if (thisIdx === idx) {
        d.classList.remove('is-active-detail');
        // eslint-disable-next-line no-unused-expressions
        d.offsetHeight;
        d.classList.add('is-active-detail');
        d.removeAttribute('aria-hidden');
      } else {
        d.classList.remove('is-active-detail');
        d.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Per-product backgrounds (product_background blocks) — toggle the
   * `is-active-pbg` class on every bg whose data-product-handle matches.
   * (There can be two per product: one desktop, one mobile.)
   */
  function setActiveProductBg(root, handle) {
    if (!handle) return false;
    var bgs = root.querySelectorAll('.custom-oled-detail__pbg');
    if (!bgs.length) return false;
    var matched = false;
    for (var i = 0; i < bgs.length; i++) {
      if (bgs[i].getAttribute('data-product-handle') === handle) {
        bgs[i].classList.add('is-active-pbg');
        matched = true;
      } else {
        bgs[i].classList.remove('is-active-pbg');
      }
    }
    return matched;
  }

  /**
   * Legacy per-slot background. Looks for a bg matching both the active
   * product handle AND the active detail index. Falls back to matching
   * by detail-index only when no per-product groups exist.
   */
  function setActiveBg(root, handle, idx) {
    if (!idx) return;
    var bgs = root.querySelectorAll('.custom-oled-detail__bg');
    if (!bgs.length) return;

    var matched = false;

    // First pass: try exact handle + index match (multi-product version)
    if (handle) {
      for (var i = 0; i < bgs.length; i++) {
        var bg = bgs[i];
        var bgHandle = bg.getAttribute('data-product-handle');
        var bgIdx = parseInt(bg.getAttribute('data-detail-index'), 10);
        if (bgHandle === handle && bgIdx === idx) {
          bg.classList.add('is-active-bg');
          matched = true;
        } else {
          bg.classList.remove('is-active-bg');
        }
      }
      if (matched) return;
    }

    // Second pass: match by detail-index only (single-set fallback)
    for (var k = 0; k < bgs.length; k++) {
      var b = bgs[k];
      var bIdx = parseInt(b.getAttribute('data-detail-index'), 10);
      if (bIdx === idx) {
        b.classList.add('is-active-bg');
      } else {
        b.classList.remove('is-active-bg');
      }
    }
  }

  function bindParallax(root) {
    // Skip parallax entirely when per-product bgs are in use — they're
    // designed to be static.
    if (root.classList.contains('has-product-bg')) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;

    var targetX = 0, targetY = 0;
    var currentX = 0, currentY = 0;
    var rafPending = false;
    var hasMoved = false;

    function loop() {
      currentX += (targetX - currentX) * 0.08;
      currentY += (targetY - currentY) * 0.08;

      root.style.setProperty('--co-mx', currentX.toFixed(3));
      root.style.setProperty('--co-my', currentY.toFixed(3));

      if (Math.abs(targetX - currentX) > 0.001 || Math.abs(targetY - currentY) > 0.001) {
        requestAnimationFrame(loop);
      } else {
        rafPending = false;
      }
    }

    function kick() {
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(loop);
      }
    }

    root.addEventListener('mousemove', function (e) {
      var rect = root.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      targetY = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      if (!hasMoved) {
        hasMoved = true;
        root.setAttribute('data-parallax', 'on');
      }
      kick();
    });

    root.addEventListener('mouseleave', function () {
      targetX = 0;
      targetY = 0;
      kick();
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-oled-detail]');
    for (var i = 0; i < roots.length; i++) {
      var el = roots[i];
      if (el.__customOledDetailInited) continue;
      el.__customOledDetailInited = true;

      bindParallax(el);

      // Mirror the hero's current accent immediately, and retry shortly
      // after to catch async image-based color extraction in the hero.
      syncAccentFromHero(el);
      requestAnimationFrame(function () { syncAccentFromHero(el); });
      setTimeout(function () { syncAccentFromHero(el); }, 800);

      (function (root) {
        var hasProductBg = root.classList.contains('has-product-bg');

        // Initialize state from the starting handle (set by Liquid based on
        // which product the PDP loaded on). The matching group and pbg are
        // already marked active in the markup; we just record their handle
        // so subsequent events can switch correctly.
        var startHandle = root.getAttribute('data-start-handle') || '';
        if (!startHandle) {
          // Fallback: use whatever group is already active in the markup
          var activeGroup = root.querySelector('.custom-oled-detail__group.is-active[data-product-handle]');
          if (activeGroup) startHandle = activeGroup.getAttribute('data-product-handle') || '';
        }

        var state = {
          handle: startHandle,
          detailIndex: 1
        };

        document.addEventListener(PRODUCT_EVENT, function (e) {
          if (!e.detail) return;
          if (e.detail.handle) {
            state.handle = e.detail.handle;
            setActiveGroup(root, e.detail.handle);
            if (hasProductBg) {
              setActiveProductBg(root, e.detail.handle);
            }
          }
          applyAccent(root, e.detail.accentColor, e.detail.accentRgb);
          if (!hasProductBg) {
            setActiveBg(root, state.handle, state.detailIndex);
          }
        });

        document.addEventListener(DETAIL_EVENT, function (e) {
          if (!e.detail || !e.detail.detailIndex) return;
          state.detailIndex = e.detail.detailIndex;
          setActiveDetail(root, e.detail.detailIndex);
          if (!hasProductBg) {
            setActiveBg(root, state.handle, state.detailIndex);
          }
        });
      })(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();